<script lang="ts" setup>
import { computed, provide, ref, watch } from "vue";
import PromiseWrapper from "@/shared/components/PromiseWrapper.vue";
import type { Nullable } from "@/models/nullable.type";
import { isNotNil } from "@/models/nullable.type";
import { cloneDeep, isEqual } from "lodash";
import { Params, PoolDetails } from "@/models/pool-details.model";
import { deserializePool } from "@/shared/utils/pool";
import { Pool } from "@/models/pool.model";
import { useSaveProbe } from "@/shared/utils/probes";
import { useSaveOut } from "@/shared/utils/outs";
import { useSavePlan } from "@/shared/utils/plans";
import LoadingOverlay from "@/shared/components/LoadingOverlay.vue";
import { lastPool } from "@/features/pool/useLastPool";
import { useRoute } from "vue-router";
import { Translation } from "vue-i18n";
import { useApi } from "@/services/useApi.ts";
import {
  storeBlePool,
  useStoredBlePool,
} from "@/features/ble/utils/localStorage.ts";
import { useBleDeviceId } from "@/features/ble/utils/useBleDeviceId.ts";
import { useGetBleErrorMessage } from "@/features/ble/utils/getBleErrorMessage.ts";
import { Device } from "@capacitor/device";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";

const emits = defineEmits<{ openSidebar: [value: void] }>();

const promise = ref<Nullable<Promise<unknown>>>(undefined);
const poolDetails = ref<Nullable<PoolDetails>>(undefined);
const pool = ref<Nullable<Pool>>(undefined);
const saving = ref(false);
const refreshing = ref(false);
const error = ref<any>(undefined);
const authError = ref<any>(undefined);
const authenticating = ref(false);

const getBleErrorMessage = useGetBleErrorMessage();

const initialPool = ref(
  poolDetails.value ? deserializePool(poolDetails.value) : undefined
);

watch(poolDetails, () => {
  initialPool.value = poolDetails.value
    ? deserializePool(poolDetails.value)
    : undefined;
});

watch(
  pool,
  () => {
    // This just helps the isPoolDirty computed property to recompute in some scenarios
  },
  { deep: true }
);

watch(initialPool, () => {
  pool.value = cloneDeep(initialPool.value);
  lastPool.value = pool.value ?? null;
});

const isPoolDirty = computed(() => !isEqual(initialPool.value, pool.value));

const api = useApi();
const route = useRoute();

const bleDeviceId = useBleDeviceId();
const storedBlePool = useStoredBlePool(bleDeviceId);

const fetchPool = async () => {
  refreshing.value = true;
  error.value = undefined;
  authError.value = null;

  if (bleDeviceId.value && !storedBlePool.value?.token) {
    authenticating.value = true;
    try {
      const deviceInfo = await Device.getInfo();
      const resp = await api.getBleToken({
        phoneId: deviceInfo.name ?? deviceInfo.model ?? deviceInfo.platform,
        MAC: "NONE",
      });

      await storeBlePool(
        bleDeviceId.value,
        { token: resp[1].token },
        { skipNickname: true }
      );
    } catch (err: any) {
      authError.value = getBleErrorMessage(err);
      refreshing.value = false;
      throw err;
    } finally {
      authenticating.value = false;
    }
  }

  return api.getPoolDetails(route.params.poolId as string).then(
    async (_poolDetails) => {
      poolDetails.value = _poolDetails;
      refreshing.value = false;
      console.log("poolDetails", poolDetails.value);
      if (bleDeviceId.value) {
        await storeBlePool(bleDeviceId.value, _poolDetails, {
          autoConnect: true,
        });
      }
    },
    (err) => {
      refreshing.value = false;
      error.value = getBleErrorMessage(err);
      throw err;
    }
  );
};

const createPromise = () => {
  promise.value = fetchPool();
};

const applyPoolChange = (fn: (pool: Pool) => Pool) => {
  initialPool.value = fn(initialPool.value!);
  pool.value = fn(pool.value!);
};

const saveOut = useSaveOut();
const savePlan = useSavePlan();
const saveProbe = useSaveProbe();

const savePool = (forceOutState = false) => {
  if (!isNotNil(pool.value)) {
    return;
  }

  saving.value = true;
  error.value = undefined;

  /* Il faut envoyer la commande "SetOut" en dernier (après le SetAutoOff et/ou le SetParam).
   C'est important dans le cas ou le "newsate" est 1 car cela déclancherai la minuterie avec la valeur précédente */

  return Promise.all([
    ...pool.value.plans
      .filter((plan, i) => !isEqual(plan, initialPool.value!.plans[i]))
      .map((plan) => savePlan(plan, pool.value!.idSystem)),
    ...pool.value.probes
      .filter((probe, i) => !isEqual(probe, initialPool.value!.probes[i]))
      .map((probe) => saveProbe(probe, pool.value!.idSystem)),
    ...Object.entries(pool.value.params)
      .filter(
        ([param, value]) =>
          !isEqual(value, initialPool.value!.params[param as keyof Params])
      )
      .map(([paramID, newValue]) =>
        api
          .setParam({
            poolID: pool.value!.idSystem,
            paramID,
            newValue,
            comMode: 1,
          })
          .then(api.waitCommand)
      ),
  ])
    .then(() =>
      Promise.all(
        Array(pool.value!.outs.length)
          .fill(0)
          .map((_, i) => i)
          .filter(
            (i) => !isEqual(pool.value!.outs[i], initialPool.value!.outs[i])
          )
          .map((i) =>
            saveOut(
              pool.value!.outs[i],
              initialPool.value!.outs[i],
              pool.value!,
              forceOutState
            )
          )
      )
    )
    .then(fetchPool)
    .then(
      () => {
        saving.value = false;
      },
      (err) => {
        saving.value = false;
        error.value = new Error(
          `Commande non exécutée, vérifiez la liaison (${err.message})`
        );
        throw err;
      }
    );
};

watch(
  () => route.params.poolId,
  () => {
    createPromise();
  }
);

watch(initialPool, () => {
  pool.value = cloneDeep(initialPool.value);
  lastPool.value = pool.value ?? null;
});

provide("pool", pool);
provide("isPoolDirty", isPoolDirty);
provide("poolDetails", poolDetails);
provide("refreshPool", fetchPool);
provide("savePool", savePool);
provide("applyPoolChange", applyPoolChange);
provide("initialPool", initialPool);

createPromise();
</script>

<template>
  <loading-overlay
    :loading="authenticating"
    :show-icon="true"
    :error="authError"
  >
    <template #message>
      <div class="flex flex-col px-5 gap-5 justify-center items-center">
        <h4 class="font-bold">
          {{ $t("BleAuthTitle") }}
        </h4>
        <div class="items-center flex flex-col gap-2">
          <Translation keypath="BleAuthMessage">
            <template #klereoSmartIcon>
              <klereo-icon name="klereo-smart" class="fill-white" />
            </template>
          </Translation>
        </div>
      </div>
    </template>
  </loading-overlay>

  <promise-wrapper
    :promise="promise"
    @retry="promise = fetchPool()"
    :allow-retry="true"
    :error-message="authError ?? error"
  >
    <loading-overlay :loading="refreshing" :show-icon="false" :error="error" />
    <loading-overlay :loading="saving" :error="error" />

    <router-view @openSidebar="$emit('openSidebar')" />
  </promise-wrapper>
</template>
